import {useCallback, useEffect, useState} from 'react';

import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import {
  landingPagePathForVariation,
  personalizedHomePageExposedKey,
  PersonalizedHomePageVariant,
  toPersonalizedHomePageVariation,
} from '@/utils/personalizedHomePage';
import {safeLocalStorage} from '@/utils/storage';

const SUBJECT_ATTRIBUTES = {skipAssignmentLogging: true};

type UsePersonalizedHomePageOptions = {
  logAssignment?: boolean;
  trackExposure?: boolean;
};

/**
 * ezx570::Discovery::PersonalizedHomePage — decides which returning-customer landing page a
 * logged-in user gets: treatment lands on `/home`, everyone else stays on `/start-new-order`.
 *
 * Assignment happens server-side in each page's `getServerSideProps` (after the logged-in check,
 * since only logged-in users are eligible) and mismatched users are redirected to the other page.
 * Exposure therefore fires on mount of whichever page root the user ended up on, for control and
 * treatment alike. The not-loaded 'fallback' arm is excluded — it isn't a real arm.
 *
 * The client re-evaluates the flag rather than reading the server's answer, which is only sound
 * while the allocation splits on the subject key alone — the server evaluates with request
 * attributes (`isLoggedIn`, `path`, `userAgent`, …) the browser doesn't send. `/experiment-audit`
 * flags this for manual confirmation in the Eppo UI.
 *
 * Callers that only want `landingPagePath` (nav links, post-action redirects) omit `trackExposure`.
 * Callers whose arms diverge on an interaction rather than on mount (the app bar logo) call the
 * returned `trackExperimentExposed` themselves; it skips the 'fallback' arm the same way.
 *
 * Client-side assignment logging is skipped by default because the landing pages already log it
 * server-side. Callers that can expose on pages with no server-side assignment (the app bar logo
 * renders everywhere) pass `logAssignment` so every exposed user also has an assignment —
 * otherwise Eppo drops them from the analysis.
 *
 * Exposure is also remembered in the browser, per subject, as `hasBeenExposed`: whether this
 * browser had seen the subject exposed to their current arm by the time the caller mounted. It's
 * `undefined` until read after mount, since the server can't see local storage.
 *
 * Server-side counterpart: `@/pageApi/getLandingPagePath`.
 */
const usePersonalizedHomePage = ({
  logAssignment = false,
  trackExposure = false,
}: UsePersonalizedHomePageOptions = {}) => {
  const {
    subject,
    variation: rawVariation,
    trackExperimentExposed: trackAnyExposure,
  } = useStringAssignmentExperimentDetails(
    EppoFlags.PersonalizedHomePage,
    logAssignment ? undefined : SUBJECT_ATTRIBUTES,
  );

  const variation = toPersonalizedHomePageVariation(rawVariation);
  const isRealArm = variation !== PersonalizedHomePageVariant.FALLBACK;

  const trackExperimentExposed = useCallback(() => {
    if (!isRealArm) return;

    trackAnyExposure();
    safeLocalStorage.setItem(personalizedHomePageExposedKey(subject), variation);
  }, [isRealArm, subject, trackAnyExposure, variation]);

  useEffect(() => {
    if (trackExposure) trackExperimentExposed();
  }, [trackExperimentExposed, trackExposure]);

  // After the exposure effect, so a page that tracks on mount reads its own exposure.
  const [exposedVariation, setExposedVariation] = useState<string | null>();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExposedVariation(safeLocalStorage.getItem(personalizedHomePageExposedKey(subject)));
  }, [subject]);

  return {
    hasBeenExposed: exposedVariation === undefined ? undefined : exposedVariation === variation,
    landingPagePath: landingPagePathForVariation(variation),
    trackExperimentExposed,
    variation,
  };
};

export default usePersonalizedHomePage;
